import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthCtx = createContext({
  token: null,
  user: null,
  ent: { credits: 0, subscription_active: false },
  register: async () => {},
  login: async () => {},
  googleExchange: async () => {},
  submitLead: async () => {},
  refreshEntitlements: async () => {},
  logout: () => {},
  ready: false,
});

const LS_TOKEN = "dc_token";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Coś poszło nie tak. Spróbuj ponownie.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN));
  const [user, setUser] = useState(null);
  const [ent, setEnt] = useState({ credits: 0, subscription_active: false });
  const [ready, setReady] = useState(false);

  const persistToken = useCallback((t) => {
    localStorage.setItem(LS_TOKEN, t);
    setToken(t);
  }, []);

  const refreshEntitlements = useCallback(async () => {
    try {
      const t = localStorage.getItem(LS_TOKEN);
      const res = await axios.get(`${API}/payments/entitlements`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnt(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  // Bootstrap: identify the current user (if a real account token exists),
  // otherwise ensure a guest token so free features keep working.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      // If returning from Google OAuth, let the callback handle the exchange.
      if (window.location.hash?.includes("session_id=")) {
        if (!cancelled) setReady(true);
        return;
      }
      const stored = localStorage.getItem(LS_TOKEN);
      if (stored) {
        try {
          const me = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${stored}` },
          });
          if (!cancelled) {
            setUser(me.data.user);
            setEnt(me.data.entitlements || { credits: 0, subscription_active: false });
            setReady(true);
          }
          return;
        } catch {
          // Not a user token (guest / expired) — fall through to guest bootstrap.
        }
      }
      try {
        const res = await axios.post(`${API}/auth/guest`);
        persistToken(res.data.token);
      } catch {/* backend offline — UI still renders */}
      finally { if (!cancelled) setReady(true); }
    }
    bootstrap();
    return () => { cancelled = true; };
  }, [persistToken]);

  const applyAuth = useCallback((data) => {
    persistToken(data.token);
    setUser(data.user);
    // Pull fresh entitlements right after auth.
    setTimeout(() => { refreshEntitlements(); }, 0);
    return data.user;
  }, [persistToken, refreshEntitlements]);

  const register = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    return applyAuth(res.data);
  }, [applyAuth]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    return applyAuth(res.data);
  }, [applyAuth]);

  const googleExchange = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    return applyAuth(res.data);
  }, [applyAuth]);

  const submitLead = useCallback(async (email) => {
    const res = await axios.post(`${API}/leads`, { email, source: "landing" });
    // keep guest/lead token only if we are not already a real user
    if (!user) persistToken(res.data.token);
    return res.data;
  }, [persistToken, user]);

  const logout = useCallback(async () => {
    localStorage.removeItem(LS_TOKEN);
    setUser(null);
    setEnt({ credits: 0, subscription_active: false });
    try {
      const res = await axios.post(`${API}/auth/guest`);
      persistToken(res.data.token);
    } catch {/* */}
  }, [persistToken]);

  return (
    <AuthCtx.Provider value={{
      token, user, ent, ready,
      register, login, googleExchange, submitLead, refreshEntitlements, logout,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function authHeaders() {
  const t = localStorage.getItem(LS_TOKEN);
  return t ? { Authorization: `Bearer ${t}` } : {};
}
